import React, { useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import responseSelector, {
  dimensionTableColumnsSelector,
} from '@store/overviewDashboard/dimensionData/selectors';
import { initDimension, fetchData } from '@store/overviewDashboard/dimensionData/actions';
import Table from '@components/common/Table';
import { useTableSort } from '@components/common/Table/hooks';
import { kpiMetricSelector } from '@store/overviewDashboard/base/selectors';
import { benchmarkStore } from '@store/overviewDashboard/benchmark/selectors';
import { Retry } from '@components/common';
import { TableWrapper } from './styled';
import { formatColumns, formatData } from './utils';
import { useInitBenchmark, useIsDimensionSupportedAndFilterApplied } from './hooks';
import TableSkeleton from '@src/components/common/Skeleton/TableSkeleton';
import useSetExport from '@src/components/export/useSetExport';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';

function DimensionTable({ dimension, unit }) {
  const dispatch = useDispatch();
  const columns = useSelector(dimensionTableColumnsSelector);
  const selectedKpiMetric = useSelector(kpiMetricSelector);
  const dimensionsStore = useSelector(responseSelector);
  const dimensionData = get(dimensionsStore, [dimension], {});
  const benchmarkData = get(useSelector(benchmarkStore), dimension, {});

  const isBenchmarkSupportedAndFilterApplied = useIsDimensionSupportedAndFilterApplied(
    dimension,
    selectedKpiMetric,
  );
  useInitBenchmark(dimension, isBenchmarkSupportedAndFilterApplied);

  useEffect(() => {
    dispatch(initDimension(dimension));
  }, [dimension]);

  const formattedColumns = useMemo(
    () =>
      formatColumns({
        dimension,
        columns,
        unit,
        benchmarkData,
        selectedKpiMetric,
        isBenchmarkSupportedAndFilterApplied,
      }),
    [
      dimension,
      columns,
      unit,
      benchmarkData,
      selectedKpiMetric,
      isBenchmarkSupportedAndFilterApplied,
    ],
  );

  const { data, isFetching, isError, isEmpty } = useMemo(
    () => formatData(dimensionData, benchmarkData, dimension, columns, unit, selectedKpiMetric),
    [dimensionData, benchmarkData, dimension, columns, unit, selectedKpiMetric],
  );

  const retryFetchData = useCallback(() => {
    dispatch(fetchData(dimension));
  }, [dimension]);

  const { sortBy, onColumnSort, sortedData } = useTableSort({
    data,
    defaultSort: { key: columns[0], order: 'desc' },
  });

  // Export data
  const dimensionMeta = useSelector(dimensionsListSelectorKeyValue);
  const dimensionName = (dimensionMeta as Record<string, string>)[dimension] || dimension;

  const exportHeaders = useMemo(() => {
    return formattedColumns.map((col, index) => ({
      key: index === 0 ? 'name' : col.key,
      title: index === 0 ? 'Name' : col.title,
    }));
  }, [formattedColumns]);

  const exportData = useMemo(() => {
    return sortedData.map((row: any) => {
      const exportRow: any = {};
      formattedColumns.forEach((col, index) => {
        if (index === 0) {
          // First column: use human-readable name
          exportRow['name'] = row.name ? row.name[0] : row[col.key];
        } else if (col.key.startsWith('benchmark.')) {
          // Benchmark column: access nested property
          const metricKey = col.key.split('.')[1];
          exportRow[col.key] =
            row.benchmark?.[metricKey] && row.benchmark[metricKey] >= 0
              ? row.benchmark[metricKey]
              : '-';
        } else {
          // Regular columns
          exportRow[col.key] = row[col.key];
        }
      });
      return exportRow;
    });
  }, [sortedData, formattedColumns]);

  // Only export when data is available and not empty
  useSetExport({
    pageKey: 'overview',
    sectionKey: 'overview-dimension-tables',
    widgetKey: `dimension-table::${dimension}`,
    widgetInfo: { title: `${dimensionName}` },
    headers: exportHeaders,
    data: !isEmpty && !isFetching ? exportData : [],
  });

  return (
    <TableWrapper style={{ height: 400 }}>
      <Retry
        fetchData={retryFetchData}
        isLoading={isFetching}
        showError={isError}
        LoadingLayer={TableSkeleton}
        isEmpty={isEmpty}
      >
        <Table
          data={sortedData}
          columns={formattedColumns}
          headerHeight={50}
          rowHeight={45}
          sortBy={sortBy}
          onColumnSort={onColumnSort}
        />
      </Retry>
    </TableWrapper>
  );
}

export default DimensionTable;
